import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Card, Select, Skeleton, useToast } from '../../design-system';
import '../styles/Chat.css';

// The Chat card on /org-admin/settings.
//
// Two things live here: the firm-wide chat configuration, and the standing
// client → employee links that decide who a new conversation lands with. A
// one-off "give this chat to someone else today" is NOT done here — that's the
// per-conversation assignment dialog on the Chat page, which deliberately does
// not disturb these links so there is always something to reset back to.

const RULES = [
  { value: 'manager', label: "The client's assigned manager" },
  { value: 'partner', label: "The client's assigned partner" },
  { value: 'explicit', label: 'Only the links set below' },
];

const SOURCE_NOTE = {
  explicit: 'linked here',
  manager: 'via assigned manager',
  partner: 'via assigned partner',
  fallback: 'via fallback',
  none: 'nobody',
};

function ChatSettingsManager() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  // clientId -> assigneeId ('' = no explicit link). Only the rows the user
  // actually touched are sent, so an admin editing one client doesn't rewrite
  // every other row's ledger.
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    apiClient
      .get('/api/chat/settings')
      .then((r) => { setData(r.data); setSettings(r.data.settings); setEdits({}); })
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to load chat settings'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoised, not `data?.users || []`: a fresh [] on every render would make
  // `nameOf` below rebuild its Map on every render too.
  const users = useMemo(() => data?.users || [], [data]);
  const canWrite = !!data?.canWrite;
  const nameOf = useMemo(() => {
    const m = new Map(users.map((u) => [u.id, u.name]));
    return (id) => (id ? m.get(id) || 'Unknown' : null);
  }, [users]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.clients || []).filter(
      (c) => !needle || c.clientName.toLowerCase().includes(needle) || (c.clientCode || '').toLowerCase().includes(needle)
    );
  }, [data, q]);

  const dirtyCount = Object.keys(edits).length;

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/chat/settings', settings);
      toast.success('Chat settings saved');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveLinks = async () => {
    setSaving(true);
    try {
      const assignments = Object.entries(edits).map(([clientId, assigneeId]) => ({
        clientId,
        assigneeId: assigneeId || null,
      }));
      const r = await apiClient.put('/api/chat/settings/defaults', { assignments });
      if (r.data.failed > 0) {
        // Partial success is the norm for a bulk write here — say what stuck
        // and what didn't rather than claiming the whole save worked.
        toast.warning(`${r.data.succeeded} saved, ${r.data.failed} skipped`);
      } else {
        toast.success(`Chat routing updated for ${r.data.succeeded} client${r.data.succeeded === 1 ? '' : 's'}`);
      }
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save the links');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><Card.Header title="💬 Client Chat" /><Skeleton.Text lines={6} /></Card>;
  if (!data) return null;

  return (
    <Card>
      <Card.Header
        title="💬 Client Chat"
        subtitle="Who handles each client's conversation, and what happens to clients you haven't linked"
      />

      {!canWrite && (
        <p className="chat-set-readonly">🔒 You can view chat routing here; changing it needs a manager or above.</p>
      )}

      {/* ── Firm-wide ── */}
      <div className="chat-set-grid">
        <label className="chat-set-toggle">
          <input
            type="checkbox"
            checked={!!settings.isEnabled}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, isEnabled: e.target.checked }))}
          />
          <span>
            <b>Chat is on for clients</b>
            <small>Turning it off hides chat in the portal. Existing conversations stay readable to your team.</small>
          </span>
        </label>

        <label className="chat-set-toggle">
          <input
            type="checkbox"
            checked={settings.allowStaffDirectMessages !== false}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, allowStaffDirectMessages: e.target.checked }))}
          />
          <span>
            <b>Staff can message each other</b>
            <small>Team Chat, separate from client conversations. Switching it off stops new messages; existing ones stay readable to the two people in them.</small>
          </span>
        </label>

        <label className="chat-set-toggle">
          <input
            type="checkbox"
            checked={!!settings.showStaffNamesToClient}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, showStaffNamesToClient: e.target.checked }))}
          />
          <span>
            <b>Show staff names to clients</b>
            <small>Off by default — clients see replies as “The firm”, matching how request comments already work.</small>
          </span>
        </label>

        <Select
          label="When a client has no link below, route to"
          value={settings.defaultRule}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({ ...s, defaultRule: e.target.value }))}
        >
          {RULES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>

        <Select
          label="Last resort (if the above finds nobody)"
          value={settings.fallbackAssigneeId || ''}
          disabled={!canWrite}
          onChange={(e) => setSettings((s) => ({ ...s, fallbackAssigneeId: e.target.value || null }))}
        >
          <option value="">— leave unassigned —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>

        <div className="ds-field chat-set-wide">
          <label className="ds-field-label" htmlFor="chat-response-note">Note shown to clients above the message box</label>
          <input
            id="chat-response-note"
            className="ds-input"
            placeholder="e.g. We usually reply within one working day."
            value={settings.responseNote || ''}
            disabled={!canWrite}
            onChange={(e) => setSettings((s) => ({ ...s, responseNote: e.target.value }))}
          />
        </div>
      </div>

      {canWrite && (
        <div className="chat-set-actions">
          <Button variant="primary" loading={saving} onClick={saveSettings}>Save chat settings</Button>
        </div>
      )}

      {/* ── Per-client links ── */}
      <h4 className="chat-set-h">Client → employee links</h4>
      <p className="chat-set-hint">
        The employee here manages that client's chat by default. Changing a link moves the live
        conversation too — unless someone has deliberately reassigned it, which is left alone.
        Every change is recorded, and the full history is on each conversation in <Link to="/org-admin/chat">Client Chat</Link>.
      </p>

      <div className="chat-set-toolbar">
        <input className="chat-search" placeholder="🔎 Filter clients…" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="chat-set-count">{rows.length} client{rows.length === 1 ? '' : 's'}</span>
        <span style={{ flex: 1 }} />
        {canWrite && dirtyCount > 0 && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setEdits({})}>Discard</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={saveLinks}>
              Save {dirtyCount} change{dirtyCount === 1 ? '' : 's'}
            </Button>
          </>
        )}
      </div>

      <div className="chat-set-scroll">
        <table className="chat-set-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Linked employee</th>
              <th>Handles chat today</th>
              <th>Managing right now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const edited = Object.prototype.hasOwnProperty.call(edits, c.clientId);
              const value = edited ? edits[c.clientId] : (c.defaultAssigneeId || '');
              return (
                <tr key={c.clientId} className={edited ? 'chat-set-dirty' : ''}>
                  <td>
                    <span className="chat-set-client">{c.clientName}</span>
                    {c.clientCode && <span className="chat-set-code">{c.clientCode}</span>}
                  </td>
                  <td>
                    <Select
                      value={value}
                      disabled={!canWrite}
                      aria-label={`Chat owner for ${c.clientName}`}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [c.clientId]: e.target.value }))}
                    >
                      <option value="">— not linked —</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </Select>
                  </td>
                  <td>
                    {c.effectiveAssigneeId
                      ? <>{nameOf(c.effectiveAssigneeId)} <span className="chat-set-src">{SOURCE_NOTE[c.source]}</span></>
                      : <span className="chat-unowned">Nobody</span>}
                  </td>
                  <td>
                    {c.currentAssigneeName || <span className="chat-unowned">—</span>}
                    {c.currentAssigneeId && c.effectiveAssigneeId && c.currentAssigneeId !== c.effectiveAssigneeId && (
                      <span className="chat-set-override" title="This conversation was reassigned by hand">overridden</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default ChatSettingsManager;
